import React from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, IconButton, Paper, Stack, Grid, Card, CardMedia } from '@mui/material';
import { CloudUpload, CameraAlt, Close } from '@mui/icons-material';
import { useSelectorArchivos } from './hooks';
import { normalizeAccept, getCameraAccept, isMobileDevice } from './helpers';

/**
 * Componente para seleccionar archivos con drag & drop
 * Compatible con Android 13+ y optimizado para captura de cámara en móviles
 */
const SelectorArchivos = ({
    onFilesChange,
    acumular = true,
    accept = { 'image/*': ['.png', '.jpg', '.jpeg'] },
    inputContent = "Arrastra una imagen o haz clic",
    inputWithFilesContent = "Añadir imágenes",
    maxFiles = 5,
    maxSize = 10485760, // 10MB por defecto
    resetKey
}) => {
    const { files, error, onDrop, removeFile } = useSelectorArchivos({
        acumular,
        maxSize,
        onFilesChange,
        resetKey
    });

    const isMobile = isMobileDevice();
    const normalizedAccept = normalizeAccept(accept);
    const cameraAccept = getCameraAccept(accept);

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop,
        accept: normalizedAccept,
        maxFiles: acumular ? maxFiles : 1,
        maxSize,
        noClick: true,
        noKeyboard: true
    });

    // Input separado para cámara en móviles (Android 13+ requiere capture explícito)
    const handleCameraCapture = (e) => {
        const capturedFiles = Array.from(e.target.files);
        if (capturedFiles.length > 0) {
            onDrop(capturedFiles, []);
        }
    };

    return (
        <Box>
            {/* Visualizador de archivos (solo en modo acumular) */}
            {acumular && files.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="textSecondary" gutterBottom>
                        {files.length} archivo{files.length > 1 ? 's' : ''} seleccionado{files.length > 1 ? 's' : ''}
                    </Typography>
                    <Grid container spacing={1} sx={{ mt: 0.5 }}>
                        {files.map((file, index) => {
                            const preview = URL.createObjectURL(file);
                            return (
                                <Grid item xs={4} sm={3} md={2} key={index}>
                                    <Card
                                        sx={{
                                            position: 'relative',
                                            boxShadow: 1
                                        }}
                                    >
                                        <CardMedia
                                            component="img"
                                            height="80"
                                            image={preview}
                                            alt={file.name}
                                            sx={{ objectFit: 'cover' }}
                                            onLoad={() => URL.revokeObjectURL(preview)}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => removeFile(file)}
                                            sx={{
                                                position: 'absolute',
                                                top: 2,
                                                right: 2,
                                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                color: '#cc071e',
                                                padding: '2px',
                                                '&:hover': {
                                                    backgroundColor: '#fff',
                                                    color: '#a00619'
                                                }
                                            }}
                                        >
                                            <Close fontSize="small" />
                                        </IconButton>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            )}

            {/* Dropzone principal */}
            <Paper
                {...getRootProps()}
                sx={{
                    p: 3,
                    border: '2px dashed',
                    borderColor: isDragActive ? '#cc071e' : '#e0e0e0',
                    backgroundColor: isDragActive ? '#fff5f5' : '#ffffff',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        borderColor: '#cc071e',
                        backgroundColor: '#fff5f5'
                    }
                }}
            >
                <input {...getInputProps()} />
                <CloudUpload sx={{ fontSize: 48, color: '#cc071e', mb: 1 }} />
                <Typography variant="body1" gutterBottom>
                    {!acumular || files.length === 0 ? inputContent : inputWithFilesContent}
                </Typography>

                {/* Botones de acción */}
                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                    <IconButton
                        onClick={open}
                        sx={{
                            border: '1px solid',
                            borderColor: '#cc071e',
                            color: '#cc071e',
                            '&:hover': {
                                backgroundColor: '#fff5f5',
                                borderColor: '#a00619'
                            }
                        }}
                    >
                        <CloudUpload />
                    </IconButton>

                    {/* Botón de cámara para móviles */}
                    {isMobile && (
                        <label htmlFor="camera-input">
                            <input
                                accept={cameraAccept}
                                id="camera-input"
                                type="file"
                                capture="environment"
                                onChange={handleCameraCapture}
                                style={{ display: 'none' }}
                            />
                            <IconButton
                                component="span"
                                sx={{
                                    border: '1px solid',
                                    borderColor: '#cc071e',
                                    color: '#cc071e',
                                    '&:hover': {
                                        backgroundColor: '#fff5f5',
                                        borderColor: '#a00619'
                                    }
                                }}
                            >
                                <CameraAlt />
                            </IconButton>
                        </label>
                    )}
                </Stack>

                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Máximo {maxFiles} archivo{maxFiles > 1 ? 's' : ''} · Hasta {maxSize / 1048576}MB
                </Typography>
            </Paper>

            {/* Error message */}
            {error && (
                <Typography sx={{ mt: 1, color: '#cc071e', fontSize: '0.875rem' }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
};

export default SelectorArchivos;
